import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Klasseneinteilung numerischer Werte (#245, `profile_bundle` 1.3).
 *
 * Der Erzeuger liefert gleich breite Klassen ueber [min, max] als
 * `{lower, upper, count, percentage}`. Klassen ohne Messung **fehlen in der
 * Liste** -- eine Klasse mit count 0 waere eine Aussage ueber Daten, die es
 * nicht gibt. Die Luecken bleiben deshalb hier sichtbar, statt sie mit Nullen
 * aufzufuellen: die Balken sitzen an ihrer Position auf der Achse, nicht
 * nebeneinander.
 *
 * Der Fall, der dieses Bild ueberhaupt erst moeglich gemacht hat: `Heart Rate`
 * (chartevents.itemid=220045) galt bis 1.2 als Kommentarfeld, weil es 125
 * verschiedene Werte fuehrt. Es sind 125 Herzfrequenzen zwischen 0 und 157.
 */
const HistogrammCard = ({ klassen }) => {
  const { t } = useTranslation();
  const eintraege = Array.isArray(klassen) ? klassen : [];

  if (eintraege.length === 0) {
    return null;
  }

  const untergrenze = Math.min(...eintraege.map((k) => k.lower));
  const obergrenze = Math.max(...eintraege.map((k) => k.upper));
  const spanne = obergrenze - untergrenze || 1;
  const groesste = Math.max(...eintraege.map((k) => k.count));

  const kurz = (w) =>
    Number.isInteger(w) ? String(w) : Number(w).toFixed(2).replace(/0$/, '');

  return (
    <Box sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, boxShadow: 1 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
        {t('dataCatalog.expanded.valueHistogram')}
      </Typography>

      <Box sx={{ position: 'relative', height: 90, borderBottom: '1px solid', borderColor: 'divider' }}>
        {eintraege.map((k, i) => {
          const links = ((k.lower - untergrenze) / spanne) * 100;
          const breite = ((k.upper - k.lower) / spanne) * 100;
          const hoehe = Math.max((k.count / groesste) * 100, 2);
          return (
            <Tooltip
              key={`${k.lower}-${i}`}
              title={`${kurz(k.lower)} – ${kurz(k.upper)}: ${t('dataCatalog.expanded.valueDistributionCount', { count: k.count })} (${k.percentage.toFixed(1)} %)`}
            >
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: `${links}%`,
                  width: `calc(${breite}% - 1px)`,
                  height: `${hoehe}%`,
                  backgroundColor: 'primary.main',
                  opacity: 0.75,
                  borderRadius: '2px 2px 0 0',
                  '&:hover': { opacity: 1 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {kurz(untergrenze)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('dataCatalog.expanded.histogramClasses', { count: eintraege.length })}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {kurz(obergrenze)}
        </Typography>
      </Box>
    </Box>
  );
};

export default HistogrammCard;
